import axios, { AxiosResponse } from 'axios';

import { Role } from 'admin/roles/Roles';
import { Tenant } from 'admin/tenants/Tenants';
import { LocaleCode } from 'core/util/Locale';
import { SearchRequest } from 'core/util/Search';

export interface User {
    id: number;
    agencyID: number;
    tenantID: number;
    email: string;
    roleIDs: string[];
    roles: Role[];
    firstName: string;
    failedEmails?: number;
    middleName: string;
    lastName: string;
    displayName: string;
    externalID: string | null;
    active: boolean;
    locale: LocaleCode;
    lastLogin?: number;
    country?: string;
    phone?: any;
    system: boolean;
    timezone?: any;
    image?: string;
    color?: string;
    initials?: string;
    tooltip?: string;
    preferences?: { [key: string]: string };
    tenant?: Tenant;
    name?: string;
}

export enum FieldName {
    ActionId = '__user_actionID__',
    Active = '__user_active__',
    AgencyId = '__user_agencyID__',
    Color = '__user_color__',
    DisplayName = '__user_displayName__',
    Email = '__user_email__',
    FirstName = '__user_firstName__',
    LastName = '__user_lastName__',
    LocationUids = '__user_locationUIDs__',
    RoleIds = '__user_roleIDs__',
    Search = '__user_search__',
    System = '__user_system__',
    TenantId = '__user_tenantID__',
    UserId = '__user_userID__',
}

export enum PreferenceKey {
    SocialPlatformTOS = 'social-platform-tos',
    SocialContentTOS = 'social-content-tos',
    GMPlatformTOS = 'gm-platform-tos',
    DefaultDashboard = 'default-dashboard2',
    DefaultReport = 'default-report2',
    PrivacyNotice = 'privacy-notice',
    UITheme = 'ui-theme',
    PrivacySettingsOptIn = 'privacy-settings-opt-in',
    Reviews3NorthStarEnabled = 'reviews3NorthStarEnabled',
    HasConfirmedAIUsage = 'hasConfirmedAiUsage',
}

export enum UITheme {
    Light = 'light',
    Dark = 'dark',
}

interface GetUsersArgs {
    basic?: boolean;
    includeManagedRoles?: boolean;
    includeRoles?: boolean;
    includeTenants?: boolean;
    excludeTenantsWithSwitchDisabled?: boolean;
    runAsAgencyUser?: boolean;
    runAsSuperAgencyUser?: boolean;
}

interface SetUserPreferenceArgs {
    preferenceKey: string;
    preferenceValue: string;
}

export interface AddUsersToRoleRequest {
    userIds: number[];
    roleId: string;
}

export interface AddUsersToRoleResponse {
    success: boolean;
    message: string;
    results: Array<{
        userId: number;
        action: 'added' | 'skipped' | 'error';
        reason: string;
        user?: User;
        processingTime: number;
    }>;
    stats: {
        totalProcessed: number;
        successCount: number;
        skippedCount: number;
        errorCount: number;
        batchesProcessed: number;
        totalTimeMs: number;
        avgTimePerUserMs: number;
    };
}

export interface RemoveRoleFromUserRequest {
    userId: number;
    roleId: string;
    replacementRoleId?: string[]; // Optional, only needed for last role scenario
}

export interface RemoveRoleFromUserResponse {
    success: boolean;
    message: string;
    user: User;
}

export const getUsers = async (
    agencyId: number,
    tenantId: number,
    search?: SearchRequest,
    args?: GetUsersArgs,
): Promise<Array<User>> => {
    const params: any = { agencyId, tenantId, search, args };

    if (args && args.runAsAgencyUser) {
        params.runAsAgencyUser = args.runAsAgencyUser;
    }

    const response: AxiosResponse = await axios.get(`/api/tenants/${tenantId}/users`, {
        params,
    });

    return response.data;
};

export const countUsers = async (agencyId: number, tenantId: number, search?: SearchRequest): Promise<number> => {
    const params: any = { agencyId, tenantId, search };

    const response: AxiosResponse = await axios.put(`/api/tenants/${tenantId}/users/count`, params);

    return response.data?.count || 0;
};

export const getUsersByEmail = async (
    agencyId: number,
    tenantId: number,
    email: string,
    args?: GetUsersArgs,
): Promise<Array<User>> => {
    const params: any = { agencyId, tenantId, email, args };

    const response: AxiosResponse = await axios.put(`/api/tenants/${tenantId}/users/email`, params);

    return response.data;
};

export const getUser = async (agencyId: number, tenantId: number, userId: number, args?: any): Promise<User> => {
    const params: any = { agencyId, tenantId, userId, args };

    const response: AxiosResponse = await axios.get(`/api/tenants/${tenantId}/users/${userId}`, { params });

    return response.data;
};

export const saveUser = async (agencyId: number, tenantId: number, user: User, args?: any): Promise<User> => {
    const response: AxiosResponse = await axios.post(`/api/tenants/${tenantId}/users`, {
        agencyId,
        tenantId,
        user,
        args,
    });

    return response.data;
};

export const deleteUser = async (agencyId: number, tenantId: number, userId: number, args?: any): Promise<string> => {
    const params: any = { agencyId, tenantId, userId, args };

    const response: AxiosResponse = await axios.delete(`/api/tenants/${tenantId}/users/${userId}`, {
        params,
    });

    return response.data;
};

export const pauseUser = async (agencyId: number, tenantId: number, userId: number, args?: any): Promise<string> => {
    const params: any = { agencyId, tenantId, userId, args };

    const response: AxiosResponse = await axios.put(`/api/tenants/${tenantId}/users/${userId}/pause`, params);

    return response.data;
};

export const getSkillJarLink = async (agencyId: number, tenantId: number, userId: number, args?: any): Promise<any> => {
    const params: any = { agencyId, tenantId, userId, args };

    const response: AxiosResponse = await axios.get(`/api/tenants/${tenantId}/users/${userId}/skill-jar`, {
        params,
    });

    return response.data;
};

export const addUsersToRole = async (
    tenantId: number,
    roleId: string,
    request: AddUsersToRoleRequest,
): Promise<AddUsersToRoleResponse> => {
    const response: AxiosResponse = await axios.post(`/api/tenants/${tenantId}/roles/${roleId}/add-users`, request);

    return response.data;
};

export const removeRoleFromUser = async (
    tenantId: number,
    userId: number,
    request: RemoveRoleFromUserRequest,
): Promise<RemoveRoleFromUserResponse> => {
    const response: AxiosResponse = await axios.post(`/api/tenants/${tenantId}/users/${userId}/remove-role`, request);

    return response.data;
};

export const setUserPreference = async (
    agencyId: number,
    tenantId: number,
    userId: number,
    args: SetUserPreferenceArgs,
): Promise<void> => {
    const params = { agencyId, tenantId, userId, args };

    const response: AxiosResponse = await axios.put(`/api/tenants/${tenantId}/users/${userId}/set-preference`, params);

    return response.data;
};
